// BroadcastChannel: Synchronizes login/logout events immediately across browser tabs.
// Web Locks API: Serializes token refresh to avoid race conditions when rotating refresh tokens.

const AUTH_CHANNEL_NAME = 'fdo_auth';
const AUTH_REFRESH_LOCK = 'fdo_auth_refresh';

export enum AUTH_BROADCAST_TYPE {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  /** A tab refreshed tokens successfully -> other tabs reschedule token refresh. */
  TOKEN_REFRESHED = 'TOKEN_REFRESHED',
}

export type TAuthBroadcastMessage = {
  type: AUTH_BROADCAST_TYPE;
};

const authChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(AUTH_CHANNEL_NAME)
    : null;

/**
 * Broadcasts a login event to other tabs.
 */
export const broadcastLogin = () => {
  authChannel?.postMessage({ type: AUTH_BROADCAST_TYPE.LOGIN });
};

/**
 * Broadcasts a logout event to other tabs.
 */
export const broadcastLogout = () => {
  authChannel?.postMessage({ type: AUTH_BROADCAST_TYPE.LOGOUT });
};

/**
 * Broadcasts a token refreshed event to other tabs.
 */
export const broadcastTokenRefreshed = () => {
  authChannel?.postMessage({ type: AUTH_BROADCAST_TYPE.TOKEN_REFRESHED });
};

/**
 * Subscribes to cross-tab authentication broadcast messages.
 *
 * @param onMessage - Callback invoked upon receiving a broadcast message.
 * @returns A cleanup function to unregister the listener.
 */
export const subscribeAuthBroadcast = (
  onMessage: (message: TAuthBroadcastMessage) => void
): (() => void) => {
  if (!authChannel) return () => undefined;

  const handler = (event: MessageEvent<TAuthBroadcastMessage>) => {
    onMessage(event.data);
  };
  authChannel.addEventListener('message', handler);
  return () => authChannel.removeEventListener('message', handler);
};

/**
 * Executes an async operation under the auth refresh Web Lock.
 * Ensures only 1 tab refreshes tokens at a time; other tabs wait and reuse the new token.
 * Falls back to direct execution if Web Locks API is unsupported.
 *
 * @param fn - Async callback to execute within the lock.
 * @returns Promise resolving to the callback result.
 */
export const withAuthRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(AUTH_REFRESH_LOCK, fn);
  }
  return fn();
};
